import { CartLinesUpdateEvent } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

class VicienciaCartUpsells extends HTMLElement {
  static pending = false;

  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.addEventListener('submit', this.add, { signal: this.abort.signal });
    this.addEventListener('change', this.changeVariant, { signal: this.abort.signal });
    this.addEventListener('click', this.navigate, { signal: this.abort.signal });
    this.addEventListener('keydown', this.onSliderKeydown, { signal: this.abort.signal });
    this.addEventListener('scroll', this.scheduleSliderSync, { signal: this.abort.signal, capture: true, passive: true });
    this.sliderObserver?.disconnect();
    this.sliderObserver = new MutationObserver(this.scheduleSliderSync);
    this.sliderObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    this.sliderResize?.disconnect();
    this.sliderResize = new ResizeObserver(this.scheduleSliderSync);
    this.sliderResize.observe(this);
    this.syncSlider();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.sliderObserver?.disconnect();
    this.sliderResize?.disconnect();
    cancelAnimationFrame(this.sliderFrame);
  }

  scheduleSliderSync = () => {
    cancelAnimationFrame(this.sliderFrame);
    this.sliderFrame = requestAnimationFrame(() => this.syncSlider());
  };

  get sliderCards() {
    return [...this.querySelectorAll('[data-upsell-card]:not([hidden])')];
  }

  syncSlider() {
    const list = this.querySelector('.vcu__list');
    const cards = this.sliderCards;
    if (!list) return;
    const rtl = getComputedStyle(list).direction === 'rtl';
    const bounds = list.getBoundingClientRect();
    const edge = rtl ? 'right' : 'left';
    let index = 0;
    let distance = Infinity;
    cards.forEach((card, i) => {
      const offset = Math.abs(card.getBoundingClientRect()[edge] - bounds[edge]);
      if (offset < distance) { distance = offset; index = i; }
    });
    this.sliderIndex = index;
    cards.forEach((card, i) => {
      card.inert = i !== index;
      card.setAttribute('aria-hidden', String(i !== index));
    });
    const navigation = this.querySelector('[data-upsell-navigation]');
    if (navigation && navigation.hidden !== (cards.length < 2)) navigation.hidden = cards.length < 2;
    const position = this.querySelector('[data-upsell-position]');
    const label = `${cards.length ? index + 1 : 0} / ${cards.length}`;
    if (position && position.textContent !== label) position.textContent = label;
    this.querySelector('[data-upsell-direction="-1"]')?.setAttribute('aria-disabled', String(index === 0));
    this.querySelector('[data-upsell-direction="1"]')?.setAttribute('aria-disabled', String(index >= cards.length - 1));
  }

  navigate = (event) => {
    const button = event.target.closest('[data-upsell-direction]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    this.goToSlide(this.sliderIndex + Number(button.dataset.upsellDirection));
  };

  onSliderKeydown = (event) => {
    if (!event.target.matches('.vcu__list')) return;
    const rtl = getComputedStyle(event.target).direction === 'rtl';
    let target;
    if (event.key === 'ArrowRight') target = this.sliderIndex + (rtl ? -1 : 1);
    if (event.key === 'ArrowLeft') target = this.sliderIndex + (rtl ? 1 : -1);
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.sliderCards.length - 1;
    if (target === undefined) return;
    event.preventDefault();
    this.goToSlide(target);
  };

  goToSlide(index) {
    const list = this.querySelector('.vcu__list');
    const cards = this.sliderCards;
    const card = cards[Math.max(0, Math.min(index, cards.length - 1))];
    if (!card || !list) return;
    const edge = getComputedStyle(list).direction === 'rtl' ? 'right' : 'left';
    const offset = card.getBoundingClientRect()[edge] - list.getBoundingClientRect()[edge];
    list.scrollTo({ left: list.scrollLeft + offset, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  changeVariant = (event) => {
    const select = event.target.closest('select[name="id"]');
    if (!select) return;
    const card = select.closest('[data-upsell-card]');
    const option = select.selectedOptions[0];
    card.querySelector('[data-upsell-price]').textContent = option.dataset.price;
    card.querySelector('[data-upsell-compare]').hidden = !option.dataset.comparePrice;
    card.querySelector('[data-upsell-compare-value]').textContent = option.dataset.comparePrice;
    card.querySelectorAll('[data-upsell-link]').forEach((link) => { link.href = option.dataset.url; });
    const image = card.querySelector('img');
    if (image && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
  };

  async getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(this.dataset.error);
    return response.json();
  }

  containsProduct(cart, productId) {
    return cart.items.some((item) => String(item.product_id) === productId ||
      item.item_components?.some((component) => String(component.product_id) === productId));
  }

  add = async (event) => {
    const form = event.target.closest('[data-upsell-form]');
    if (!form) return;
    event.preventDefault();
    event.stopPropagation();
    if (VicienciaCartUpsells.pending || this.recoveryRequired) return;
    VicienciaCartUpsells.pending = true;
    const card = form.closest('[data-upsell-card]');
    const productId = card.dataset.productId;
    const variantId = new FormData(form).get('id');
    const button = form.querySelector('button');
    const originalLabel = button.textContent;
    const controls = [...this.querySelectorAll('button, select')];
    controls.forEach((control) => { control.disabled = true; });
    this.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.adding;
    const error = this.querySelector('[data-upsell-error]');
    error.hidden = true;
    let deferred;
    let mutationStarted = false;
    let mutationRejected = false;
    try {
      const before = await this.getCart();
      if (this.containsProduct(before, productId)) {
        card.hidden = true;
        this.hideIfEmpty();
        await sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'hydration' });
        return;
      }
      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      }));
      const sections = [...new Set(['cart-drawer-section', ...Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (element) => element.dataset.sectionId)])].slice(0, 5);
      mutationStarted = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        mutationRejected = true;
        throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
      }
      const cart = await this.getCart();
      card.hidden = true;
      this.hideIfEmpty();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, sections: result.sections, source: 'viciencia-cart-upsells', didError: false },
      });
    } catch (failure) {
      deferred?.reject(failure);
      if (mutationStarted && !mutationRejected) {
        this.recoveryRequired = true;
        error.textContent = this.dataset.refresh;
        this.querySelector('[data-upsell-recovery]').hidden = false;
      } else {
        error.textContent = failure.message || this.dataset.error;
      }
      this.hidden = false;
      error.hidden = false;
    } finally {
      VicienciaCartUpsells.pending = false;
      this.removeAttribute('aria-busy');
      controls.forEach((control) => { control.disabled = Boolean(this.recoveryRequired); });
      button.textContent = originalLabel;
      this.syncSlider();
    }
  };

  hideIfEmpty() {
    this.hidden = !this.querySelector('[data-upsell-card]:not([hidden])');
    this.syncSlider();
  }
}

if (!customElements.get('viciencia-cart-upsells')) {
  customElements.define('viciencia-cart-upsells', VicienciaCartUpsells);
}
